import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuthStore, selectUser, selectSetUser } from '../stores/authStore'
import Sidebar, { BottomNav } from './Sidebar'
import { isBottomNavRoute } from '../utils/bottomNav'
import { useTheme } from '../hooks/useTheme'
import { useSwipeNavigation } from '../hooks/useSwipeNavigation'
import { useNeighbourPrefetch } from '../hooks/useNeighbourPrefetch'
import ScreenHeaderBar from './ScreenHeaderBar'
import Toast from './shared/Toast'
import NotificationPrompt from './shared/NotificationPrompt'
import QuickLookupPalette from './QuickLookupPalette'
import { useLookupPaletteStore } from '../stores/lookupPaletteStore'
import { initPlausible, trackPageView } from '../utils/analytics'
import { APP_SCROLL_ID } from '../utils/appScroll'
import { fireDayReturnEvents } from '../utils/funnelEvents'
import { useLocale } from '../i18n/context'
import apiClient from '../api/client'
import type { User } from '../types'

export default function Layout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const { theme, toggle: toggleTheme, setTheme } = useTheme()
  const user    = useAuthStore(selectUser)
  const setUser = useAuthStore(selectSetUser)
  const location = useLocation()
  const { tr }  = useLocale()

  // Palette state lives in a Zustand store so any component (e.g. Decks page
  // Quick Lookup button) can trigger it without prop drilling.
  const { isOpen: paletteOpen, layout: paletteLayout, initialOcrFile, initialQuery: paletteInitialQuery, openAsModal, close: closePalette, clearOcrFile } =
    useLookupPaletteStore()

  // Cmd/Ctrl+K: toggle palette as centered modal; skip when user is typing
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'k') return
      const tag = (document.activeElement?.tagName ?? '').toLowerCase()
      const editable = (document.activeElement as HTMLElement)?.isContentEditable
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || editable) return
      e.preventDefault()
      if (paletteOpen) closePalette()
      else openAsModal()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [paletteOpen, openAsModal, closePalette])

  // Banner is session-only (not persisted) so it reappears on next login if mismatch persists
  const [explBannerDismissed, setExplBannerDismissed] = useState(false)
  const showExplBanner =
    !explBannerDismissed &&
    !!user?.explanation_language &&
    !!user?.source_language &&
    user.explanation_language !== user.source_language

  const handleRevertExplLang = async () => {
    if (!user?.source_language) return
    try {
      const { data } = await apiClient.patch<User>('/api/v1/auth/me', {
        explanation_language: user.source_language,
      })
      setUser(data)
      setExplBannerDismissed(true)
    } catch {
      // silent — user can try again
    }
  }

  // Inject Plausible script once on mount
  useEffect(() => { initPlausible() }, [])

  // Fire a page view on every route change (SPA navigation)
  useEffect(() => { trackPageView(location.pathname) }, [location.pathname])

  // Check day-1 / day-7 return events once per authenticated session.
  // Keyed on user.id so it re-runs only when a different user logs in.
  useEffect(() => {
    if (!user) return
    fireDayReturnEvents(user, setUser).catch(() => {})
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Compute trial days remaining (null when no active trial or user is paid Pro)
  const trialDaysLeft = useMemo(() => {
    if (!user?.trial_ends_at || user.plan === 'pro') return null
    const msLeft = new Date(user.trial_ends_at).getTime() - Date.now()
    if (msLeft <= 0) return null
    return Math.ceil(msLeft / (1000 * 60 * 60 * 24))
  }, [user?.trial_ends_at, user?.plan])

  // One-time toast when the user has an active trial — shown once ever per user (localStorage flag)
  useEffect(() => {
    if (!user?.id || user.plan === 'pro' || trialDaysLeft === null) return
    const key = `cs_trial_toast_${user.id}`
    if (localStorage.getItem(key)) return
    localStorage.setItem(key, '1')
    const days = trialDaysLeft
    const msg = tr.layout.trialToast(days)
    const tid = setTimeout(() => { toast(msg, { duration: 7000 }) }, 2000)
    return () => clearTimeout(tid)
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const navReserved = isBottomNavRoute(location.pathname)

  // ADR-106 — swipe navigation across the six top-level stops (mobile, touch).
  // The page content below follows the finger; `touchAction` keeps vertical
  // scroll and pinch-zoom with the browser while the gesture is live.
  const swipeContentRef = useRef<HTMLDivElement>(null)
  const { touchAction: swipeTouchAction } = useSwipeNavigation(swipeContentRef)

  // The data each chain stop's neighbours show first is loaded in the
  // background once this stop has settled, so arriving there — by swipe or by
  // tap, on any width — shows content rather than a skeleton. Data only:
  // nothing is rendered in advance (utils/neighbourPrefetch.ts).
  useNeighbourPrefetch()

  // The one place a route's page-container width is decided (see
  // index.css's --page-max-width-narrow/-wide) — a screen never declares
  // its own cap. Library and Progress are lists/stat columns that read
  // top-to-bottom and are named explicitly as narrow. Home is deliberately
  // NOT here even though it's one of this task's four named screens: it
  // already sizes its own content (a single centered hero card, or a
  // two-column layout up to 860px when there's a secondary block) via its
  // own inner max-width classes, unrelated to and untouched by this
  // container — narrowing the outer column to 680px would clip that
  // two-column layout rather than give Home "a width suited to what it
  // holds," so it keeps the wide default like every other route this task
  // doesn't name, which is what it already had.
  const NARROW_ROUTES = ['/library', '/progress']
  const pageMaxWidth = NARROW_ROUTES.includes(location.pathname)
    ? 'var(--page-max-width-narrow)'
    : 'var(--page-max-width-wide)'

  // `100dvh`, not `100vh`: the large-viewport unit does not shrink for an
  // on-screen keyboard even where `interactive-widget=resizes-content`
  // (index.html) makes the actual visible area smaller, so `100vh` pushed
  // this root's containing blocks below the fold by exactly the keyboard's
  // height — see
  // docs/diagnostics/composer-keyboard-positioning-measurement-2026-07-26.md
  // §1. `100dvh` tracks the real visible area on both open and closed states.
  return (
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>
      {/* ── Sidebar (desktop) ─────────────────────────────────────────── */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        theme={theme}
        onSetTheme={setTheme}
      />

      {/* ── Right column ──────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Screen-header bar — desktop only; carries the current screen's own
            identity + tools (see components/ScreenHeaderBar.tsx). Bottom nav
            carries identity on mobile. */}
        <ScreenHeaderBar />

        {/* Explanation language mismatch banner */}
        {showExplBanner && user && (
          <ExplanationLangBanner
            explanationLang={user.explanation_language!}
            sourceLang={user.source_language!}
            onRevert={handleRevertExplLang}
            onDismiss={() => setExplBannerDismissed(true)}
          />
        )}

        {/* Main content — the app's single scrolling surface (ADR-067
            Decision 3). Pages must not open a scrollport of their own inside
            it; see utils/appScroll.ts for how they drive this one. */}
        {/* Mobile: clear the bottom nav (60px) + the ring's 12px protrusion + 8px buffer = 80px + safe-area.
            On routes with no bottom nav (ADR-067 Decision 1 — the deck screen)
            that reservation is dead space at the end of the page, and the
            deck's own pinned element sizes its own clearance, so it is not
            applied there. Gated on the route only, never on the transient
            hide reasons, so the page cannot reflow while the keyboard opens.
            Desktop gets none of this: the base padding below is the same 40px
            as every other side, and only the mobile media query adds the
            bottom-nav reservation back — it used to be a bare `80` in the
            inline style, applying on every width including desktop, where it
            rendered as a dead grey strip pinned across the bottom of the one
            scrollport (`--bg-page`) and, because `useStickyPaneBox` (now `useScrollportMetrics`) read this
            same padding to size the deck screen's sticky pane, silently
            shrank that pane's height by the same 80px. */}
        <style>{`@media(max-width:767px){.layout-main-content--nav{padding-bottom:calc(80px + env(safe-area-inset-bottom,0px))!important}.layout-main-content--nonav{padding-bottom:env(safe-area-inset-bottom,0px)!important}}`}</style>
        <main
          id={APP_SCROLL_ID}
          style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: 'var(--bg-page)',
            padding: 'var(--space-10)',
            touchAction: swipeTouchAction,
          }}
          className={`layout-main-content ${navReserved ? 'layout-main-content--nav' : 'layout-main-content--nonav'} max-md:!px-4 max-md:!pt-6`}
        >
          {/* The page-container width for the current route (chosen just
              above — DESIGN_SYSTEM.md "Desktop page container"), defined
              once here rather than each route capping its own content at
              its own number. A single shared width for every screen was
              tried and reverted: it made Library's deck rows into wide,
              near-empty bands with nothing to their right. This keeps the
              part that was correct — one decision, one place — while
              letting a list-shaped screen and the deck screen's two-pane
              editor take the widths their content actually needs. */}
          <div ref={swipeContentRef} data-swipe-region="app" style={{ maxWidth: pageMaxWidth, margin: '0 auto' }}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* ── Mobile bottom nav ──────────────────────────────────────────── */}
      <BottomNav theme={theme} onToggleTheme={toggleTheme} />

      {/* ── Toast notifications ────────────────────────────────────────── */}
      <Toast />

      {/* ── Push notification opt-in prompt ────────────────────────────── */}
      <NotificationPrompt />

      {/* ── Quick Lookup palette (Cmd/Ctrl+K on desktop; FAB on mobile) ── */}
      <QuickLookupPalette
        open={paletteOpen}
        onClose={() => { closePalette(); clearOcrFile() }}
        layout={paletteLayout}
        initialOcrFile={initialOcrFile}
        initialQuery={paletteInitialQuery ?? undefined}
      />
    </div>
  )
}

// ── Explanation language mismatch banner ──────────────────────────────────────

interface ExplBannerProps {
  explanationLang: string
  sourceLang: string
  onRevert: () => void
  onDismiss: () => void
}

// Compact language-code badge — replaces emoji flags per Wave 4 emoji ban.
function LangCodeBadge({ code }: { code: string }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '1px 6px',
      borderRadius: 4,
      backgroundColor: 'var(--bg-secondary)',
      border: '1px solid var(--border)',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.05em',
      color: 'var(--text-primary)',
      fontFamily: 'var(--font-ui)',
      verticalAlign: 'middle',
    }}>
      {code.toUpperCase()}
    </span>
  )
}

function ExplanationLangBanner({ explanationLang, sourceLang, onRevert, onDismiss }: ExplBannerProps) {
  const { tr } = useLocale()

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border)',
        padding: '8px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
        fontSize: 13,
        color: 'var(--text-secondary)',
      }}
    >
      <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
        {tr.layout.explBanner} <LangCodeBadge code={explanationLang} />
      </span>
      <button
        onClick={onRevert}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 12px',
          borderRadius: 50,
          border: '1px solid var(--border)',
          backgroundColor: 'var(--bg-page)',
          color: 'var(--text-primary)',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'var(--font-ui)',
          whiteSpace: 'nowrap',
        }}
      >
        {tr.layout.revertExpl} <LangCodeBadge code={sourceLang} />
      </button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-secondary)',
          fontSize: 16,
          lineHeight: 1,
          padding: '2px 4px',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        ×
      </button>
    </div>
  )
}
